import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@env/environment';
import { User, HomeXpertLink, CreateHomeXpertLinkRequest } from '../models/user.model';

export interface HomeXpertLinkStatus {
  isLinked: boolean;
  externalUsername: string | null;
  linkedAt: string | null;
  isTokenValid: boolean;
}

export interface InitiateLinkResponse {
  authorizationUrl: string;
  state: string;
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly API_URL = `${environment.apiUrl}/users`;
  private readonly AUTH_URL = `${environment.apiUrl}/auth`;

  constructor(private http: HttpClient) {}

  getCurrentUser(): Observable<User> {
    return this.http.get<User>(`${this.API_URL}/me`);
  }

  updateProfile(data: { 
    firstName?: string; 
    lastName?: string; 
    currentPassword?: string; 
    newPassword?: string; 
    preferredLanguage?: string;
    street?: string;
    zipCode?: string;
    city?: string;
    country?: string;
    companyName?: string;
    vatId?: string;
  }): Observable<User> {
    return this.http.put<User>(`${this.API_URL}/me`, data);
  }

  getHomeXpertLink(): Observable<HomeXpertLink> {
    return this.http.get<HomeXpertLink>(`${this.API_URL}/me/homexpert-link`);
  }

  createHomeXpertLink(request: CreateHomeXpertLinkRequest): Observable<HomeXpertLink> {
    return this.http.post<HomeXpertLink>(`${this.API_URL}/me/homexpert-link`, request);
  }

  revokeHomeXpertLink(reason?: string): Observable<any> {
    return this.http.delete(`${this.API_URL}/me/homexpert-link`, { params: reason ? { reason } : {} });
  }

  // OAuth-based homeXpert linking
  getHomeXpertLinkStatus(): Observable<HomeXpertLinkStatus> {
    return this.http.get<HomeXpertLinkStatus>(`${this.AUTH_URL}/link/homexpert/status`);
  }

  initiateHomeXpertLink(returnUrl?: string): Observable<InitiateLinkResponse> {
    return this.http.post<InitiateLinkResponse>(`${this.AUTH_URL}/link/homexpert`, { returnUrl });
  }

  unlinkHomeXpert(): Observable<any> {
    return this.http.delete(`${this.AUTH_URL}/link/homexpert`);
  }

  refreshHomeXpertToken(): Observable<any> {
    return this.http.post(`${this.AUTH_URL}/link/homexpert/refresh`, {});
  }

  requestEmailChange(newEmail: string): Observable<any> {
    return this.http.post(`${environment.apiUrl}/users/me/change-email`, { newEmail });
  }

  uploadProfileImage(file: File): Observable<User> {
    const formData = new FormData();
    formData.append('image', file);
    return this.http.post<User>(`${this.API_URL}/me/profile-image`, formData);
  }

  deleteProfileImage(): Observable<User> {
    return this.http.delete<User>(`${this.API_URL}/me/profile-image`);
  }
}
